/**
 * AI Assistant Page — Data Terminal Chat Interface
 *
 * Professional data-terminal styled chat interface for AI-powered
 * portfolio analysis queries. Not bubble-style — clean, dense layout.
 *
 * Features:
 *   - Message list with distinct system vs user styling
 *   - Suggested question chips from /ai/suggestions
 *   - Active context display (fund/investee)
 *   - Conversation list sidebar (past conversations)
 *   - POST /ai/query for new messages
 *
 * Enterprise+ feature gate.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Send,
  Loader2,
  Bot,
  User,
  MessageSquare,
  Plus,
  ChevronRight,
  Briefcase,
  Building2,
  Lock,
  Sparkles,
} from 'lucide-react';
import { clsx } from 'clsx';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ──────────────────────────────────────────────────────

interface AIConversation {
  id: string;
  title: string;
  created_at: string;
  message_count: number;
}

interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: {
    sources?: string[];
    confidence?: number;
  };
}

interface AISuggestion {
  text: string;
  category: string;
}

interface AIContext {
  fund_id?: number;
  fund_name?: string;
  investee_id?: number;
  investee_name?: string;
}

interface AIQueryResponse {
  message: AIMessage;
  context?: AIContext;
}

// ─── Data Fetching ──────────────────────────────────────────────

function useConversations() {
  return useQuery<AIConversation[]>({
    queryKey: ['ic', 'ai', 'conversations'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/ai/conversations');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
  });
}

function useConversationMessages(conversationId: string | null) {
  return useQuery<AIMessage[]>({
    queryKey: ['ic', 'ai', 'messages', conversationId],
    queryFn: async () => {
      const res = await apiClient.get(
        `/api/v1/impact-core/ai/conversations/${conversationId}/messages`,
      );
      return res.data?.data ?? res.data ?? [];
    },
    enabled: conversationId !== null,
    staleTime: 10_000,
  });
}

function useSuggestions() {
  return useQuery<AISuggestion[]>({
    queryKey: ['ic', 'ai', 'suggestions'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/ai/suggestions');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 300_000,
  });
}

// ─── Main Component ─────────────────────────────────────────────

export function AIAssistantPage() {
  const { user } = useAuth();
  const tier = user?.subscription_tier ?? 'free';
  const isEnterprise = tier === 'enterprise';

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [context, setContext] = useState<AIContext>({});
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const conversationsQuery = useConversations();
  const messagesQuery = useConversationMessages(activeConversationId);
  const suggestionsQuery = useSuggestions();

  // Sync loaded messages
  useEffect(() => {
    if (messagesQuery.data) {
      setMessages(Array.isArray(messagesQuery.data) ? messagesQuery.data : []);
    }
  }, [messagesQuery.data]);

  const sendMutation = useMutation<AIQueryResponse, Error, string>({
    mutationFn: async (query: string) => {
      const res = await apiClient.post('/api/v1/impact-core/ai/query', {
        query,
        conversation_id: activeConversationId,
        context,
      });
      return res.data?.data ?? res.data;
    },
    onSuccess: (response) => {
      // The API returns {answer, intent, confidence, data, suggestions, context, ...}
      // after unwrapping (res.data?.data ?? res.data). Handle both shapes:
      // 1. AIQueryResponse shape: {message: AIMessage, context?: AIContext}
      // 2. Unwrapped shape: {answer: string, intent: string, context?: AIContext, ...}
      if (response?.message && typeof response.message === 'object' && 'content' in response.message) {
        setMessages((prev) => [...prev, response.message]);
      } else {
        const answer = (response as any)?.answer
          || (response as any)?.data?.answer
          || 'No response received.';
        const assistantMessage: AIMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: typeof answer === 'string' ? answer : JSON.stringify(answer),
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
      if (response?.context) {
        setContext(response.context);
      }
    },
  });

  const handleSend = useCallback(
    (text: string) => {
      if (!text.trim() || sendMutation.isPending) return;

      const userMessage: AIMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: text.trim(),
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      sendMutation.mutate(text.trim());
    },
    [sendMutation],
  );

  const handleNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setContext({});
  }, []);

  const handleSelectConversation = useCallback((id: string) => {
    setActiveConversationId(id);
  }, []);

  if (!isEnterprise) {
    return <FeatureGate />;
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-0 overflow-hidden rounded-lg border border-ic-border">
      {/* Sidebar */}
      {sidebarOpen && (
        <ConversationSidebar
          conversations={conversationsQuery.data ?? []}
          activeId={activeConversationId}
          isLoading={conversationsQuery.isLoading}
          onSelect={handleSelectConversation}
          onNew={handleNewConversation}
        />
      )}

      {/* Main Chat Area */}
      <div className="flex flex-1 flex-col bg-ic-surface">
        {/* Chat Header */}
        <ChatHeader
          context={context}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
        />

        {/* Messages */}
        <MessageList
          messages={messages}
          isLoading={messagesQuery.isLoading && activeConversationId !== null}
          isPending={sendMutation.isPending}
        />

        {/* Suggestions (shown when no messages) */}
        {messages.length === 0 && !messagesQuery.isLoading && (
          <SuggestionChips
            suggestions={suggestionsQuery.data ?? []}
            onSelect={handleSend}
          />
        )}

        {/* Input */}
        <ChatInput
          onSend={handleSend}
          disabled={sendMutation.isPending}
        />
      </div>
    </div>
  );
}

// ─── Feature Gate ───────────────────────────────────────────────

function FeatureGate() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ic-accent/10">
          <Lock className="h-6 w-6 text-ic-accent" />
        </div>
        <h2 className="text-lg font-semibold text-ic-text-primary">AI Assistant</h2>
        <p className="mt-2 text-sm text-ic-text-tertiary">
          The AI Assistant is available on the Enterprise tier. Ask natural-language questions
          about your portfolio, ESG metrics, and compliance status.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-ic-border bg-ic-card px-3 py-2 text-xs font-medium text-ic-text-tertiary">
          <Lock className="h-3.5 w-3.5" />
          Enterprise Feature
        </div>
      </div>
    </div>
  );
}

// ─── Conversation Sidebar ───────────────────────────────────────

interface SidebarProps {
  conversations: AIConversation[];
  activeId: string | null;
  isLoading: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
}

function ConversationSidebar({ conversations, activeId, isLoading, onSelect, onNew }: SidebarProps) {
  return (
    <div className="flex w-56 flex-col border-r border-ic-border bg-ic-card">
      <div className="flex items-center justify-between border-b border-ic-border p-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
          Conversations
        </span>
        <button
          onClick={onNew}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-ic-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5 text-ic-text-tertiary" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-slate-700/50" />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <p className="p-2 text-center text-[10px] text-ic-text-tertiary">
            No conversations yet
          </p>
        ) : (
          <div className="space-y-1">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => onSelect(conv.id)}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors',
                  activeId === conv.id
                    ? 'bg-ic-accent/10 text-ic-accent'
                    : 'text-ic-text-secondary hover:bg-ic-hover',
                )}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate text-xs">{conv.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Chat Header ────────────────────────────────────────────────

interface ChatHeaderProps {
  context: AIContext;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

function ChatHeader({ context, sidebarOpen, onToggleSidebar }: ChatHeaderProps) {
  const hasContext = context.fund_name ?? context.investee_name;

  return (
    <div className="flex items-center gap-3 border-b border-ic-border px-4 py-3">
      <button
        onClick={onToggleSidebar}
        className="flex h-7 w-7 items-center justify-center rounded-md border border-ic-border hover:bg-ic-hover transition-colors"
      >
        <ChevronRight
          className={clsx(
            'h-3.5 w-3.5 text-ic-text-tertiary transition-transform',
            sidebarOpen && 'rotate-180',
          )}
        />
      </button>
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-ic-accent" />
        <span className="text-sm font-medium text-ic-text-primary">AI Assistant</span>
      </div>

      {hasContext && (
        <div className="ml-auto flex items-center gap-2">
          {context.fund_name && (
            <span className="flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-400">
              <Briefcase className="h-3 w-3" />
              {context.fund_name}
            </span>
          )}
          {context.investee_name && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400">
              <Building2 className="h-3 w-3" />
              {context.investee_name}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Message List ───────────────────────────────────────────────

interface MessageListProps {
  messages: AIMessage[];
  isLoading: boolean;
  isPending: boolean;
}

function MessageList({ messages, isLoading, isPending }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isPending]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ic-accent/10">
          <Bot className="h-5 w-5 text-ic-accent" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-ic-text-primary">
            Impact Core AI Assistant
          </p>
          <p className="mt-1 max-w-xs text-xs text-ic-text-tertiary">
            Ask questions about your portfolio, ESG metrics, compliance status,
            or investment performance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-1 p-4">
        {messages.map((msg) => (
          <MessageRow key={msg.id} message={msg} />
        ))}
        {isPending && (
          <div className="flex items-center gap-3 py-3 px-4">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-ic-accent/10">
              <Bot className="h-3.5 w-3.5 text-ic-accent" />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-ic-text-tertiary" />
              <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-ic-text-tertiary [animation-delay:150ms]" />
              <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-ic-text-tertiary [animation-delay:300ms]" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// ─── Message Row (Terminal style) ───────────────────────────────

function MessageRow({ message }: { message: AIMessage }) {
  const isUser = message.role === 'user';

  return (
    <div
      className={clsx(
        'flex items-start gap-3 rounded-md py-3 px-4',
        isUser ? 'bg-transparent' : 'bg-ic-card/50',
      )}
    >
      <div
        className={clsx(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded',
          isUser ? 'bg-slate-600/30' : 'bg-ic-accent/10',
        )}
      >
        {isUser ? (
          <User className="h-3.5 w-3.5 text-ic-text-secondary" />
        ) : (
          <Bot className="h-3.5 w-3.5 text-ic-accent" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
            {isUser ? 'You' : 'Assistant'}
          </span>
          <span className="text-[9px] text-ic-text-tertiary/50">
            {formatTime(message.timestamp)}
          </span>
        </div>
        <div className="mt-1 text-sm leading-relaxed text-ic-text-primary whitespace-pre-wrap">
          {message.content}
        </div>
        {message.metadata?.sources && message.metadata.sources.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {message.metadata.sources.map((src, idx) => (
              <span
                key={idx}
                className="rounded bg-slate-700/50 px-1.5 py-0.5 text-[9px] text-ic-text-tertiary"
              >
                {src}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Suggestion Chips ───────────────────────────────────────────

interface SuggestionChipsProps {
  suggestions: AISuggestion[];
  onSelect: (text: string) => void;
}

function SuggestionChips({ suggestions, onSelect }: SuggestionChipsProps) {
  if (suggestions.length === 0) return null;

  return (
    <div className="border-t border-ic-border/50 px-4 py-3">
      <p className="mb-2 text-[10px] uppercase tracking-wider text-ic-text-tertiary">
        Suggested Questions
      </p>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((suggestion, idx) => (
          <button
            key={idx}
            onClick={() => onSelect(suggestion.text)}
            className="rounded-full border border-ic-border bg-ic-card px-3 py-1.5 text-xs text-ic-text-secondary hover:border-ic-accent/50 hover:text-ic-accent transition-colors"
          >
            {suggestion.text}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Chat Input ─────────────────────────────────────────────────

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled: boolean;
}

function ChatInput({ onSend, disabled }: ChatInputProps) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    if (input.trim() && !disabled) {
      onSend(input);
      setInput('');
    }
  }, [input, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <div className="border-t border-ic-border bg-ic-card p-3">
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your portfolio, ESG metrics, compliance..."
          rows={1}
          className="flex-1 resize-none rounded-md border border-ic-border bg-ic-surface px-3 py-2.5 text-sm text-ic-text-primary placeholder:text-ic-text-tertiary/50 focus:border-ic-accent focus:outline-none"
        />
        <button
          onClick={handleSubmit}
          disabled={!input.trim() || disabled}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-ic-accent text-white hover:bg-ic-accent/90 disabled:opacity-40 transition-colors"
        >
          {disabled ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Utilities ──────────────────────────────────────────────────

function formatTime(timestamp: string): string {
  try {
    const d = new Date(timestamp);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}
